import { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Users, CalendarClock, Trash2, PencilLine, Plus, GraduationCap, CircleUser, MapPin,
} from "lucide-react";

// Team Meetings — schedule meetings/trainings, mark attendance, and let
// the Productivity report's Comments column surface "Meeting: 45 min"
// entries per agent per day. Permission: `manage_team_meetings` OR admin
// (server enforces the same gate). Non-managers can still SEE the list
// but the create/edit/delete/attendance buttons are hidden.
const KINDS = [
  { key: "meeting",  label: "Meeting",  icon: Users },
  { key: "training", label: "Training", icon: GraduationCap },
];

function nowLocalIso() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16); // YYYY-MM-DDTHH:MM
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
}

export default function TeamMeetings() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [canManage, setCanManage] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // meeting doc or null
  const [attendanceOpen, setAttendanceOpen] = useState(null); // meeting doc or null
  const [agents, setAgents] = useState([]);      // {id, name, email, role}[]

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/permissions/effective");
        const pp = data.page_permissions || {};
        setCanManage(!!(pp.admin === true || pp.manage_team_meetings === true));
      } catch (e) { console.debug(e?.message); }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter === "all" ? {} : { status: statusFilter };
      const { data } = await api.get("/team-meetings", { params });
      setRows(data.meetings || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load meetings");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  // Load agents once — used by the picker
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/users");
        setAgents((data || []).map((u) => ({
          id: u.id, name: u.name || u.email, email: u.email, role: u.role_label || u.role,
        })));
      } catch (e) { void e; }
    })();
  }, []);

  const openNew = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (m) => { setEditing(m); setModalOpen(true); };
  const del = async (m) => {
    if (!window.confirm(`Delete "${m.title}"?`)) return;
    try {
      await api.delete(`/team-meetings/${m.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete");
    }
  };

  return (
    <div className="space-y-4" data-testid="team-meetings">
      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="inline-flex rounded-sm border border-border overflow-hidden">
          {["all", "scheduled", "completed", "cancelled"].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              className={`px-3 h-9 text-sm capitalize border-r border-border last:border-r-0 ${statusFilter === s ? "bg-primary text-primary-foreground" : "bg-background hover:bg-secondary/60"}`}
              data-testid={`meetings-filter-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {canManage && (
            <Button onClick={openNew} className="rounded-sm h-9" data-testid="meeting-new-btn">
              <Plus size={14} className="mr-1" /> New meeting
            </Button>
          )}
        </div>
      </div>

      {/* Grid */}
      <div className="overflow-auto border border-border rounded-sm">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60 text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">Title</th>
              <th className="px-3 py-2 text-left">Kind</th>
              <th className="px-3 py-2 text-left">When</th>
              <th className="px-3 py-2 text-left">Duration</th>
              <th className="px-3 py-2 text-left">Attendees</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Created by</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground">No meetings yet.</td></tr>
            )}
            {rows.map((m) => (
              <tr key={m.id} className="border-t border-border hover:bg-secondary/30" data-testid={`meeting-row-${m.id}`}>
                <td className="px-3 py-2 font-medium">{m.title}</td>
                <td className="px-3 py-2 capitalize">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-sm ${m.kind === "training" ? "bg-purple-100 text-purple-800" : "bg-cyan-100 text-cyan-800"}`}>
                    {m.kind === "training" ? <GraduationCap size={11}/> : <Users size={11}/>}
                    {m.kind}
                  </span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap">{fmtDateTime(m.scheduled_at)}</td>
                <td className="px-3 py-2 whitespace-nowrap">{m.duration_min} min</td>
                <td className="px-3 py-2">{(m.attendee_user_ids || []).length}</td>
                <td className="px-3 py-2 capitalize">
                  <span className={`inline-flex items-center px-2 py-0.5 text-[11px] rounded-sm ${
                    m.status === "completed" ? "bg-emerald-100 text-emerald-800" :
                    m.status === "cancelled" ? "bg-rose-100 text-rose-800" :
                    "bg-slate-100 text-slate-700"
                  }`}>{m.status}</span>
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{m.created_by_name || m.created_by || "—"}</td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex gap-1">
                    {canManage && m.status !== "completed" && (
                      <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px]" onClick={() => setAttendanceOpen(m)} data-testid={`meeting-attendance-${m.id}`}>
                        <CircleUser size={12} className="mr-1" /> Mark attendance
                      </Button>
                    )}
                    {canManage && (
                      <>
                        <Button size="sm" variant="ghost" className="h-7 rounded-sm text-[11px]" onClick={() => openEdit(m)} data-testid={`meeting-edit-${m.id}`}>
                          <PencilLine size={12} />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 rounded-sm text-[11px] hover:text-rose-600" onClick={() => del(m)} data-testid={`meeting-delete-${m.id}`}>
                          <Trash2 size={12} />
                        </Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <MeetingModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onSaved={() => { setModalOpen(false); load(); }}
          editing={editing}
          agents={agents}
        />
      )}
      {attendanceOpen && (
        <AttendanceModal
          meeting={attendanceOpen}
          agents={agents}
          onClose={() => setAttendanceOpen(null)}
          onSaved={() => { setAttendanceOpen(null); load(); }}
        />
      )}
    </div>
  );
}

// ---------- Create/Edit modal ----------
function MeetingModal({ open, onClose, onSaved, editing, agents }) {
  const [title, setTitle] = useState(editing?.title || "");
  const [kind, setKind] = useState(editing?.kind || "meeting");
  const [scheduledAt, setScheduledAt] = useState(
    editing?.scheduled_at?.slice(0, 16) || nowLocalIso(),
  );
  const [duration, setDuration] = useState(editing?.duration_min || 60);
  const [attendeeIds, setAttendeeIds] = useState(new Set(editing?.attendee_user_ids || []));
  const [notes, setNotes] = useState(editing?.notes || "");
  const [location, setLocation] = useState(editing?.location || "");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const filtered = useMemo(() => {
    const t = q.toLowerCase();
    if (!t) return agents;
    return agents.filter((a) =>
      (a.name || "").toLowerCase().includes(t)
      || (a.email || "").toLowerCase().includes(t)
      || (a.role || "").toLowerCase().includes(t),
    );
  }, [q, agents]);

  const toggle = (id) => {
    setAttendeeIds((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const save = async () => {
    if (!title.trim()) { toast.error("Title required"); return; }
    if (!scheduledAt) { toast.error("Scheduled date required"); return; }
    if (attendeeIds.size === 0 && !window.confirm("No attendees selected — save anyway?")) return;
    setBusy(true);
    try {
      // Convert local datetime-local input into full ISO with local TZ offset
      const payload = {
        title: title.trim(),
        kind, scheduled_at: new Date(scheduledAt).toISOString(),
        duration_min: Math.max(5, Math.min(480, parseInt(duration, 10) || 60)),
        attendee_user_ids: Array.from(attendeeIds),
        notes, location,
      };
      if (editing?.id) await api.patch(`/team-meetings/${editing.id}`, payload);
      else await api.post("/team-meetings", payload);
      toast.success(editing?.id ? "Meeting updated" : "Meeting scheduled");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editing?.id ? "Edit meeting" : "Schedule new meeting"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Weekly sales sync" data-testid="meeting-title" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Kind</Label>
              <div className="flex gap-1 mt-1">
                {KINDS.map((k) => (
                  <button key={k.key} type="button" onClick={() => setKind(k.key)}
                    className={`px-3 py-2 rounded-sm text-xs border ${kind === k.key ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:bg-secondary/60"}`}
                    data-testid={`meeting-kind-${k.key}`}
                  >
                    <k.icon size={12} className="inline mr-1" />{k.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>When</Label>
              <Input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} data-testid="meeting-scheduled-at" />
            </div>
            <div>
              <Label>Duration (min)</Label>
              <Input type="number" min="5" max="480" value={duration} onChange={(e) => setDuration(e.target.value)} data-testid="meeting-duration" />
            </div>
          </div>
          <div>
            <Label className="flex items-center gap-1"><MapPin size={12} /> Location / Meet link</Label>
            <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Boardroom 2, or https://meet.google.com/xxx" />
          </div>
          <div>
            <Label>Attendees ({attendeeIds.size} selected)</Label>
            <Input placeholder="Search by name / email / role" value={q} onChange={(e) => setQ(e.target.value)} className="mb-2" data-testid="meeting-attendee-search" />
            <div className="max-h-48 overflow-y-auto border border-border rounded-sm">
              {filtered.map((a) => (
                <label key={a.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-secondary/60 cursor-pointer text-sm">
                  <Checkbox checked={attendeeIds.has(a.id)} onCheckedChange={() => toggle(a.id)} data-testid={`meeting-attendee-${a.id}`} />
                  <span className="flex-1">{a.name}</span>
                  <span className="text-xs text-muted-foreground">{a.role} · {a.email}</span>
                </label>
              ))}
              {filtered.length === 0 && <div className="px-3 py-4 text-xs text-center text-muted-foreground">No matches.</div>}
            </div>
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="meeting-save">
            <CalendarClock size={14} className="mr-1" /> {busy ? "Saving…" : editing?.id ? "Update" : "Schedule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Attendance modal ----------
function AttendanceModal({ meeting, agents, onClose, onSaved }) {
  const [rows, setRows] = useState(() =>
    (meeting.attendee_user_ids || []).map((uid) => {
      const prev = (meeting.attendance || {})[uid] || {};
      return {
        user_id: uid,
        attended: prev.attended !== false,
        minutes: prev.minutes ?? meeting.duration_min,
      };
    })
  );
  const [busy, setBusy] = useState(false);
  const nameFor = (uid) => agents.find((a) => a.id === uid) || { name: uid, email: "" };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/team-meetings/${meeting.id}/attendance`, rows);
      toast.success("Attendance recorded");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Mark attendance — {meeting.title}</DialogTitle></DialogHeader>
        <div className="space-y-1 max-h-96 overflow-y-auto">
          {rows.length === 0 && <div className="text-sm text-muted-foreground text-center py-8">No attendees to mark.</div>}
          {rows.map((r, i) => {
            const a = nameFor(r.user_id);
            return (
              <div key={r.user_id} className="flex items-center gap-2 px-2 py-1.5 border-b border-border last:border-0">
                <Checkbox checked={r.attended} onCheckedChange={(v) => {
                  setRows((prev) => prev.map((x, j) => j === i ? { ...x, attended: !!v } : x));
                }} data-testid={`attendance-checkbox-${r.user_id}`} />
                <span className="flex-1 text-sm">{a.name} <span className="text-xs text-muted-foreground">· {a.email}</span></span>
                <Input type="number" min="0" max="480" className="w-20 h-8" value={r.minutes}
                  disabled={!r.attended}
                  onChange={(e) => {
                    const n = parseInt(e.target.value, 10) || 0;
                    setRows((prev) => prev.map((x, j) => j === i ? { ...x, minutes: n } : x));
                  }}
                  data-testid={`attendance-minutes-${r.user_id}`}
                />
                <span className="text-xs text-muted-foreground">min</span>
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="attendance-save">
            {busy ? "Saving…" : "Save & complete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
